import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useRestaurantNav } from '../../hooks/useRestaurantNav';
import { fetchWeeklyIdeasRequest } from '../../redux/slices/weeklyIdeasSlice';
import { fetchSeasonalIdeasRequest } from '../../redux/slices/seasonalIdeasSlice';
import { fetchCompetitiveIdeasRequest } from '../../redux/slices/competitiveIdeasSlice';
import { fetchGeneratedIdeasRequest } from '../../redux/slices/generatedIdeasSlice';
import { triggerContentGenerationRequest } from '../../redux/slices/contentGenerationSlice';
import type { RootState } from '../../redux/store';
import LoadingState from '../common/LoadingState';
import ErrorState from '../common/ErrorState';
import EmptyState from '../common/EmptyState';
import PageHeader from '../common/PageHeader';
import CreateCampaignDrawer from '../campaingIdeas/CreateCampaignDrawer';
import CampaignIdeaSkeleton from './CampaignIdeaSkeleton';
import { Md1kPlus } from 'react-icons/md';

interface CombinedIdea {
  id: string;
  title: string;
  type: string;
  date: string;
  eventName: string;
  badge: 'weekly' | 'seasonal' | 'competitive' | 'custom';
  rawData: any; // Store the original idea data
}

const Campaigns: React.FC = () => {
  const dispatch = useDispatch();
  const { navigate } = useRestaurantNav();
  const restaurantId = useSelector((state: RootState) => state.clover.restaurantId);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const {
    data: weeklyIdeas,
    loading: weeklyLoading,
    error: weeklyError,
  } = useSelector((state: RootState) => state.weeklyIdeas);

  const {
    data: seasonalIdeas,
    loading: seasonalLoading,
    error: seasonalError,
  } = useSelector((state: RootState) => state.seasonalIdeas);

  const {
    data: competitiveIdeas,
    loading: competitiveLoading,
    error: competitiveError,
  } = useSelector((state: RootState) => state.competitiveIdeas);

  const {
    ideas: generatedCampaignIdeas,
    loading: generatedLoading,
    error: generatedError,
  } = useSelector((state: RootState) => state.generatedIdeas);

  useEffect(() => {
    if (restaurantId) {
      dispatch(fetchWeeklyIdeasRequest(restaurantId));
      dispatch(fetchSeasonalIdeasRequest(restaurantId));
      dispatch(fetchCompetitiveIdeasRequest(restaurantId));
      dispatch(fetchGeneratedIdeasRequest({ restaurantId }));
    }
  }, [dispatch, restaurantId]);

  // Combine all ideas into a single array
  const combinedIdeas: CombinedIdea[] = [
    ...weeklyIdeas.map((idea) => ({
      id: `weekly-${idea.idea_id}`,
      title: idea.idea_headline,
      type: idea.idea_type,
      date: new Date(idea.trigger_event_date).toLocaleDateString(),
      eventName: idea.trigger_event_name,
      badge: 'weekly' as const,
      rawData: idea,
    })),
    ...seasonalIdeas.map((idea) => ({
      id: `seasonal-${idea.suggestion_id}`,
      title: idea.suggestion_name,
      type: idea.suggestion_type,
      date: new Date(idea.created_at).toLocaleDateString(),
      eventName: idea.target_season || 'Seasonal',
      badge: 'seasonal' as const,
      rawData: idea,
    })),
    ...competitiveIdeas.map((idea) => ({
      id: `competitive-${idea.idea_id}`,
      title: idea.idea_title,
      type: idea.idea_category,
      date: new Date(idea.created_at).toLocaleDateString(),
      eventName: 'Market Insight',
      badge: 'competitive' as const,
      rawData: idea,
    })),
    ...generatedCampaignIdeas.map((idea) => ({
      id: `custom-${idea.id}`,
      title: idea.idea_headline,
      type: idea.idea_type,
      date: new Date(idea.created_at).toLocaleDateString(),
      eventName: idea.trigger_event_name || 'Custom',
      badge: 'custom' as const,
      rawData: idea,
    })),
  ];

  const {
    loading: contentGenerating,
    flyerDetails,
    ideaId,
    ideaType,
  } = useSelector((state: RootState) => state.contentGeneration);

  // Navigate to flyer page when flyer details are loaded with idea_id and idea_type
  useEffect(() => {
    if (flyerDetails && !contentGenerating) {
      // Build URL with query parameters using ideaId and ideaType from Redux
      const params = new URLSearchParams();
      if (ideaId) params.append('ideaId', ideaId.toString());
      if (ideaType) params.append('ideaType', ideaType);
      
      const url = params.toString() ? `/flyer?${params.toString()}` : '/flyer';
      navigate(url);
    }
  }, [flyerDetails, contentGenerating, ideaId, ideaType, navigate]);

  const handleIdeaClick = (idea: CombinedIdea) => {
    // Extract numeric ID from string ID (e.g., "weekly-101" -> 101)
    const numericId = parseInt(idea.id.split('-')[1]);
    
    // Map badge type to idea_type
    let ideaType = "MANUAL_IDEA";
    switch (idea.badge) {
      case 'weekly':
        ideaType = "WEEKLY_IDEA";
        break;
      case 'seasonal':
        ideaType = "SEASONAL_IDEA";
        break;
      case 'competitive':
        ideaType = "GENERAL_IDEA";
        break;
      case 'custom':
        ideaType = "MANUAL_IDEA";
        break;
    }
    
    // Build payload with only idea_id, idea_type, and task_type filled
    const payload = {
      idea_id: numericId,
      idea_type: ideaType,
      idea: "",
      task_type: "SOCIAL_MEDIA",
      campaign_goal: "",
      quarterly_budget: "",
      weekly_promotional_goal: "",
      blog_post_goal: "",
      target_keyword: ""
    };
    
    if (!restaurantId) return;
    dispatch(triggerContentGenerationRequest({ restaurantId, payload }));
  };

  // Show full page loading only when generating content
  if (contentGenerating) return <LoadingState message="Generating content..." />;
  
  // Check if ALL APIs failed
  const allFailed = weeklyError && seasonalError && competitiveError && generatedError;
  if (allFailed) return <ErrorState message="Failed to load campaign ideas" />;
  
  // Check if all APIs finished loading and still no data
  const allFinished = !weeklyLoading && !seasonalLoading && !competitiveLoading && !generatedLoading;
  const isEmpty = combinedIdeas.length === 0;
  if (isEmpty && allFinished) return <EmptyState message="No campaign ideas available" />;

  const getBadgeColor = (type: 'weekly' | 'seasonal' | 'competitive' | 'custom') => {
    switch (type) {
      case 'weekly':
        return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      case 'seasonal':
        return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'competitive':
        return 'bg-purple-500/20 text-purple-400 border-purple-500/30';
      case 'custom':
        return 'bg-orange-500/20 text-orange-400 border-orange-500/30';
    }
  };

  const getBadgeLabel = (type: 'weekly' | 'seasonal' | 'competitive' | 'custom') => {
    switch (type) {
      case 'weekly':
        return 'Weekly Ideas';
      case 'seasonal':
        return 'Seasonal Ideas';
      case 'competitive':
        return 'Competitive Ideas';
      case 'custom':
        return 'Custom Ideas';
    }
  };

  return (
    <div className="min-h-screen">
      {/* Header with Create Campaign button */}
      <div className="flex items-center justify-end mb-6">
        
        <button
          onClick={() => setIsDrawerOpen(true)}
          className="bg-[#EE4423] hover:bg-[#ff5533] cursor-pointer text-white px-6 py-2 rounded-full font-medium transition-colors"
        >
          + Create Campaign
        </button>
      </div>

      {/* All Ideas in 4-column grid */}
      <div className=" mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Render actual ideas */}
          {combinedIdeas.map((idea) => (
            <div
              key={idea.id}
              onClick={() => handleIdeaClick(idea)}
              className="bg-[#2a2a2a] rounded-lg p-4 border border-gray-700 hover:border-[#EE4423] transition-all cursor-pointer"
            >
              <span className={`px-2 py-.5 rounded-full text-[10px] font-medium border ${getBadgeColor(idea.badge)} inline-block mb-3`}>
                {getBadgeLabel(idea.badge)}
              </span>
              <div className="text-gray-400 text-[10px] mb-2">{idea.date}</div>
              <h3 className="text-white font-medium mb-2 text-sm line-clamp-2">{idea.title}</h3>
              <div className="text-gray-400 text-xs mb-1">{idea.eventName}</div>
              <div className="text-[#EE4423] text-xs italic">{idea.type}</div>
            </div>
          ))}
          
          {/* Loading skeletons for pending APIs */}
          {weeklyLoading && <CampaignIdeaSkeleton badgeColor="blue" badgeWidth="w-24" />}
          {seasonalLoading && <CampaignIdeaSkeleton badgeColor="green" badgeWidth="w-28" />}
          {competitiveLoading && <CampaignIdeaSkeleton badgeColor="purple" badgeWidth="w-32" />}
          {generatedLoading && <CampaignIdeaSkeleton badgeColor="orange" badgeWidth="w-24" />}
        </div>
      </div>

      {/* Create Campaign Drawer */}
      <CreateCampaignDrawer isOpen={isDrawerOpen} onClose={() => setIsDrawerOpen(false)} />
    </div>
  );
};

export default Campaigns;
